import React from "react";
import { typography, surfaces, borders, textColors } from "../../design-system/tokens";
import { useDefaultsEdit } from "./DefaultsEditContext";
import { EditContext } from "../context/EditContext";
import { AddElementContext } from "../context/AddElementContext";

import Hero from "../../renderer/components/Hero";
import Component from "../../renderer/components/Component";
import Title from "../../renderer/components/Title";
import Text from "../../renderer/components/Text";
import Button from "../../renderer/components/Button";

const PREVIEW_CONFIG = {
  component: { scale: 0.85, minWidth: 520 },
  hero: { scale: 0.75, minWidth: 720 },
  title: { scale: 0.95, minWidth: 520 },
  text: { scale: 0.9, minWidth: 520 },
  button: { scale: 0.95, minWidth: 520 },
};

class PreviewErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch() {
    // Swallow: preview should never break the editor UX.
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className={`${typography.bodySmall} ${textColors.muted}`}>
          Preview unavailable for this item.
        </div>
      );
    }
    return this.props.children;
  }
}

export default function DefaultsPreviewPane() {
  const { previewObject, entryLoading } = useDefaultsEdit();
  const [previewBg, setPreviewBg] = React.useState("light"); // 'light' | 'dark'
  const containerRef = React.useRef(null);
  const [measuredWidth, setMeasuredWidth] = React.useState(960);

  React.useEffect(() => {
    if (!containerRef.current || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const nextWidth = entry.contentRect.width;
      setMeasuredWidth((prev) => {
        if (!Number.isFinite(nextWidth)) return prev;
        return Math.abs(prev - nextWidth) > 1 ? nextWidth : prev;
      });
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  if (entryLoading && !previewObject) {
    return <div className={`${typography.bodySmall} ${textColors.muted}`}>Loading preview…</div>;
  }

  if (!previewObject || !previewObject.type) {
    return (
      <div className={`${typography.bodySmall} ${textColors.muted}`}>
        Select an entry to preview it.
      </div>
    );
  }

  const type = String(previewObject.type);
  const cfg = PREVIEW_CONFIG[type];
  if (!cfg) {
    return (
      <div className={`${typography.bodySmall} ${textColors.muted}`}>
        No preview available for this item.
      </div>
    );
  }

  const effectiveWidth = Math.max(measuredWidth, cfg.minWidth);

  const renderPreview = () => {
    if (type === "component") return <Component item={previewObject} containerWidth={effectiveWidth} />;
    if (type === "hero") return <Hero item={previewObject} containerWidth={effectiveWidth} />;
    if (type === "title") return <Title item={previewObject} />;
    if (type === "text") return <Text item={previewObject} />;
    if (type === "button") return <Button item={previewObject} />;
    return null;
  };

  // Fixed preview backgrounds regardless of app theme.
  // - light: subtle light canvas
  // - dark: same dark-gray surface we used before (but without relying on `dark:`)
  const previewBgClass = previewBg === "dark" ? "bg-dark-900" : "bg-gray-50";

  return (
    <div ref={containerRef} className="flex flex-col gap-3 h-full">
      <div className="flex items-center justify-between">
        <button
          type="button"
          className={`p-2 rounded-lg border ${borders.default} ${surfaces.primary} ${textColors.primary} hover:bg-gray-100`}
          onClick={() => setPreviewBg((p) => (p === "light" ? "dark" : "light"))}
          aria-label="Toggle preview background"
          title="Toggle preview background"
        >
          {previewBg === "light" ? (
            // Moon icon (switch to dark preview background)
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
              />
            </svg>
          ) : (
            // Sun icon (switch to light preview background)
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
          )}
        </button>
      </div>

      <div
        className={`relative flex-1 w-full overflow-hidden rounded-lg border ${borders.default} ${previewBgClass} flex items-start justify-center`}
      >
        <div
          className="pointer-events-none flex justify-center w-full [&>*]:!p-0 [&>*]:!m-0 py-6"
          style={{
            transform: `scale(${cfg.scale})`,
            transformOrigin: "top center",
            width: `${100 / cfg.scale}%`,
          }}
        >
          {/* Ensure renderer components don't think they're in editor selection mode */}
          <PreviewErrorBoundary>
            <AddElementContext.Provider value={{ enabled: true, openAddModal: () => {} }}>
              <EditContext.Provider value={null}>
                {/* In a flex-centered preview stage, some renderer roots (e.g. Component) can shrink to 0 width
                    when they have no intrinsic content. Force a full-width wrapper so container backgrounds render. */}
                <div className="w-full">{renderPreview()}</div>
              </EditContext.Provider>
            </AddElementContext.Provider>
          </PreviewErrorBoundary>
        </div>
      </div>
    </div>
  );
}

